import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { Observable, of } from "rxjs";
import { AccountsService } from "../accounts.service";
import { PermissionResolverService } from "../permissions/permission-resolver.service";
import { LandingRouteService } from "../permissions/landing-route.service";
import { first, map, switchMap } from "rxjs/operators";
import { normalizePermissionKeys } from "../permissions/permission-route.helper";

/** Permission fields copied from app `Route.data` into feature modules via ancestor chain. */
export interface PermissionRouteData {
  permissionSectionBase?: string;
  readPermission?: string[];
  writePermission?: string[];
}

/**
 * Walks from `route` up to the root and returns the first `data` that declares
 * any permission field (same segment as app-routing `finalizeFeatureRoute`).
 */
export function findPermissionDataInAncestors(
  route: ActivatedRouteSnapshot | null
): PermissionRouteData {
  let r: ActivatedRouteSnapshot | null = route;
  while (r) {
    const d = r.data || {};
    const readPermission = normalizePermissionKeys(
      d["readPermission"] as string[] | undefined
    );
    const writePermission = normalizePermissionKeys(
      d["writePermission"] as string[] | undefined
    );
    const permissionSectionBase =
      typeof d["permissionSectionBase"] === "string"
        ? d["permissionSectionBase"]
        : undefined;
    if (
      permissionSectionBase ||
      readPermission.length > 0 ||
      writePermission.length > 0
    ) {
      return {
        permissionSectionBase,
        readPermission: readPermission.length ? readPermission : undefined,
        writePermission: writePermission.length ? writePermission : undefined,
      };
    }
    r = r.parent;
  }
  return {};
}

@Injectable({
  providedIn: "root",
})
export class HasPermissionGuard implements CanActivate {
  constructor(
    private readonly accounts: AccountsService,
    private readonly router: Router,
    private readonly permissionResolver: PermissionResolverService,
    private readonly landingRoute: LandingRouteService
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    const fromTree = findPermissionDataInAncestors(next);
    const permissionSectionBase = fromTree.permissionSectionBase;
    const readKeys = fromTree.readPermission ?? [];
    const requireEdit = next.data?.["permissionRequireEdit"] === true;

    return this.accounts.login$.pipe(
      first(),
      switchMap((login) => {
        if (!login) {
          return of(this.router.parseUrl("/login"));
        }
        const decide = () =>
          this.allowOrFallback(
            permissionSectionBase,
            readKeys,
            requireEdit,
            _state.url
          );
        if (this.accounts.hasCachedPermissions()) {
          return of(decide());
        }
        return this.accounts.cachedPermissions$.pipe(
          first(),
          map(() => decide())
        );
      })
    );
  }

  private allowOrFallback(
    permissionSectionBase: string | undefined,
    readKeys: string[],
    requireEdit: boolean,
    attemptedUrl: string
  ): boolean | UrlTree {
    if (!permissionSectionBase && readKeys.length === 0) {
      return true;
    }
    if (permissionSectionBase) {
      const allowed = requireEdit
        ? this.permissionResolver.canEditSection(permissionSectionBase)
        : this.permissionResolver.canViewSection(permissionSectionBase);
      return allowed
        ? true
        : this.router.parseUrl(this.landingRoute.path(attemptedUrl));
    }
    const allowed = this.accounts.hasAnyPermission(readKeys);
    return allowed
      ? true
      : this.router.parseUrl(this.landingRoute.path(attemptedUrl));
  }
}
